import { Component, OnInit, computed, inject, input, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ImageFallbackDirective } from '../img-fallback.directive';
import { Project, Participant, Report, Package, Assessment } from '../models';
import { countBy, filter, get, size } from 'lodash-es';
import { LocaleNamePipe } from '../locale-name-pipe';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { ReportDomainService } from '../domain-services/report-domain.service';
import { AssessmentDomainService } from '../domain-services/assessment-domain.service';
import { PackageDomainService } from '../domain-services/package-domain.service';

@Component({
  selector: 'app-project-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    RouterModule,
    MatDividerModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ImageFallbackDirective,
    RouterLink,
    LocaleNamePipe,
    RouterOutlet,
  ],
  templateUrl: './project-detail.component.html',
  styleUrls: ['./project-detail.component.scss'],
})
export class ProjectDetailComponent implements OnInit {
  reports = input.required<Report[]>();
  packages = input.required<Package[]>();

  private readonly projectDomain = inject(ProjectDomainService);
  private readonly reportDomain = inject(ReportDomainService);
  private readonly assessmentDomain = inject(AssessmentDomainService);
  private readonly packageDomain = inject(PackageDomainService);
  readonly route = inject(ActivatedRoute);

  // New notification email being added
  addNotificationEmail = signal<string>('');

  // Prefer the signal-based current project; fall back to
  // the resolver data for the initial navigation.
  project = computed<Project>(() => {
    const current = this.projectDomain.currentProject();
    if (current) {
      return current;
    }
    return this.route.snapshot.data['project'] as Project;
  });

  projectReports = computed<Report[]>(() => {
    const fromDomain = this.reportDomain.projectReports();
    if (fromDomain) {
      return fromDomain;
    }
    return this.reports();
  });

  projectPackages = computed<Package[]>(() => {
    const fromDomain = this.packageDomain.projectPackages();
    if (fromDomain) {
      return fromDomain;
    }
    return this.packages();
  });

  participantCount = computed(() => {
    const allAssessments = this.assessmentDomain.allAssessments();
    if (!allAssessments) {
      return 0;
    }

    const projectAssessments = filter(allAssessments, { projectId: this.project().id });
    return size(countBy(projectAssessments, 'participantId'));
  });

  unclaimedIdsCount = computed(() => this.projectDomain.unclaimedIdsCount());

  ngOnInit(): void {
    const projectId = this.project()?.id ?? this.route.snapshot.paramMap.get('projectId');
    if (projectId) {
      this.projectDomain.loadProjectUnclaimedIds(projectId).subscribe();
    }
  }

  downloadUnclaimedIds(): void {
    const projectId = this.project()?.id ?? this.route.snapshot.paramMap.get('projectId');
    if (!projectId) {
      return;
    }

    this.projectDomain.downloadProjectUnclaimedIdsCsv(projectId).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'unused-ids.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  }

  removeNotificationEmail(slot: 1 | 2 | 3 | 4): void {
    const current = this.project();
    const field = `email${slot}` as 'email1' | 'email2' | 'email3' | 'email4';
    const updated: Project = { ...current, [field]: null };

    this.projectDomain.updateProject(updated).subscribe();
  }

  onAddNotificationEmailInput(value: string): void {
    this.addNotificationEmail.set(value);
  }

  addNotificationEmailClick(): void {
    const value = this.addNotificationEmail().trim();
    if (!value || !this.isValidEmail(value)) {
      return;
    }

    const current = this.project();
    const updated: Project = { ...current };

    const fields: Array<'email1' | 'email2' | 'email3' | 'email4'> = [
      'email1',
      'email2',
      'email3',
      'email4',
    ];

    let assigned = false;
    for (const field of fields) {
      if (!updated[field]) {
        updated[field] = value;
        assigned = true;
        break;
      }
    }

    if (!assigned) {
      return;
    }

    this.projectDomain.updateProject(updated).subscribe(() => {
      this.addNotificationEmail.set('');
    });
  }

  private isValidEmail(email: string): boolean {
    // Simple email pattern: some chars + @ + some chars + . + some chars
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  get = get;
}
